/**
 * NotFoundPage — TREATX-SEO-RECOVERY-S3A
 *
 * Before S3A every unknown URL rendered the HOMEPAGE at HTTP 200 (the catch-all
 * route was `<Navigate to="/" replace />`). To a crawler that is an unbounded
 * supply of duplicate homepages, each one self-canonicalising to `/`.
 *
 * This page replaces that, on the PUBLIC surface only:
 *   - it says it is a 404, in words, to the visitor;
 *   - it emits `robots: noindex, follow` so the URL leaves the index;
 *   - it emits `prerender-status-code: 404` so the prerender layer in front of
 *     Production can return a REAL HTTP 404 instead of 200.
 *
 * It deliberately offers the visitor the public routes that do exist, rather
 * than bouncing them somewhere they did not ask for.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, Search, Stethoscope } from 'lucide-react';
import SEO from '../components/SEO';

const NotFoundPage = () => {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const lang = (i18n?.language || 'en').split('-')[0];

  return (
    <div
      className="min-h-[60vh] flex flex-col items-center justify-center px-6 py-20 text-center"
      data-testid="not-found-page"
    >
      <SEO
        title="Page Not Found"
        description="This page does not exist on TreatX. Browse verified treatments and clinics instead."
        noindex
        statusCode={404}
        lang={lang}
        locale={lang === 'tr' ? 'tr_TR' : 'en_US'}
      />

      <p
        className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-400"
        data-testid="not-found-status"
      >
        404
      </p>
      <h1
        className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl"
        data-testid="not-found-heading"
      >
        This page doesn&apos;t exist
      </h1>
      <p className="mt-4 max-w-xl text-base text-slate-600" data-testid="not-found-body">
        We couldn&apos;t find anything at{' '}
        <span className="font-mono text-slate-500">{pathname}</span>. It may have moved, or the
        link may be incomplete.
      </p>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-slate-700"
          data-testid="not-found-home-link"
        >
          <Home className="h-4 w-4" aria-hidden="true" />
          Go to homepage
        </Link>
        <Link
          to="/treatments"
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:border-slate-900 hover:text-slate-900"
          data-testid="not-found-treatments-link"
        >
          <Stethoscope className="h-4 w-4" aria-hidden="true" />
          Browse treatments
        </Link>
        <Link
          to="/find-clinics"
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:border-slate-900 hover:text-slate-900"
          data-testid="not-found-clinics-link"
        >
          <Search className="h-4 w-4" aria-hidden="true" />
          Find a clinic
        </Link>
      </div>
    </div>
  );
};

export default NotFoundPage;
